/**
 * usePDFExport.js — Week 4
 *
 * Generates a bilingual (EN/AR) PDF for a campaign decision.
 *
 * Strategy:
 *   - Dynamically loads jsPDF from CDN at first call (cached on window)
 *   - Arabic text rendered right-to-left with proper alignment
 *   - Embeds NexusFlow branding header
 *   - Sections: header, verdict badge, KPIs table, analysis, recommended actions
 *   - File named:  nexusflow-{campaignName}-{YYYY-MM-DD}.pdf
 *
 * Usage:
 *   const { generatePDF, generating } = usePDFExport();
 *   await generatePDF(decision, campaign, lang);
 */

import { useState, useCallback } from "react";
import { channelDisplay } from "../lib/channels";

/* ── KPI formatters (mirror DecisionResult) ─────────────── */
const KPI_LABELS = {
  en: {
    roas: "ROAS", profit: "Profit", cpl: "CPL", cpa: "CPA", cac: "CAC",
    ctr: "CTR", cpc: "CPC", cpm: "CPM", customers: "Customers",
    conversionPct: "Conversion %", conversionRate: "Conversion rate",
    conversion_rate: "Conversion rate", avg_order_value: "Avg Order",
  },
  ar: {
    roas: "العائد على الإنفاق الإعلاني", profit: "الربح",
    cpl: "تكلفة العميل المحتمل", cpa: "تكلفة الاكتساب", cac: "تكلفة اكتساب العميل",
    ctr: "نسبة النقر", cpc: "تكلفة النقرة", cpm: "تكلفة الألف ظهور",
    customers: "العملاء الدافعون", conversionPct: "نسبة التحويل",
    conversionRate: "معدل التحويل", conversion_rate: "معدل التحويل",
    avg_order_value: "متوسط قيمة الطلب",
  },
};

function fmtKPI(key, val, lang = "en") {
  if (typeof val !== "number") return String(val);
  if (key === "roas")                         return `${val.toFixed(2)}x`;
  if (key === "conversion_rate" || key === "conversionRate" || key === "conversionPct" || key === "ctr") return `${val.toFixed(1)}%`;
  if (key === "customers")                    return `${Math.round(val)}`;
  if (["profit","cpl","cpa","cac","cpc","cpm","avg_order_value"].includes(key)) {
    // Currency: ر.س for Arabic, SAR for English (was hardcoded "$").
    const currency = lang === "ar" ? "ر.س" : "SAR";
    return `${Math.abs(val).toFixed(2)} ${currency}`;
  }
  return val.toFixed(2);
}

function getKPILabel(key, lang) {
  return KPI_LABELS[lang]?.[key] || KPI_LABELS.en[key] || key.replace(/_/g," ").toUpperCase();
}

/* ── Load jsPDF from CDN ─────────────────────────────────── */
const CDN_URL = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";

function loadJsPDF() {
  return new Promise((resolve, reject) => {
    if (window.jspdf?.jsPDF) { resolve(window.jspdf.jsPDF); return; }
    const s = document.createElement("script");
    s.src = CDN_URL;
    s.onload  = () => resolve(window.jspdf?.jsPDF);
    s.onerror = () => reject(new Error("Failed to load jsPDF"));
    document.head.appendChild(s);
  });
}

/* ── Colour config ───────────────────────────────────────── */
const VERDICT_COLORS = {
  CONTINUE: { r: 34,  g: 197, b: 94  }, // green-500
  PAUSE:    { r: 234, g: 179, b: 8   }, // yellow-500
  STOP:     { r: 239, g: 68,  b: 68  }, // red-500
};

const BRAND = { r: 59, g: 130, b: 246 }; // blue-500

/* ── Main PDF builder ────────────────────────────────────── */
async function buildPDF(decision, campaign, lang) {
  const JsPDF = await loadJsPDF();
  if (!JsPDF) throw new Error("PDF library unavailable");

  const isAR   = lang === "ar";
  const doc    = new JsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const PW     = doc.internal.pageSize.getWidth();   // 210
  const PH     = doc.internal.pageSize.getHeight();  // 297
  const ML     = 20; // left margin
  const MR     = 20; // right margin
  const CW     = PW - ML - MR;  // content width
  let   y      = 20; // cursor

  const col = ({ r, g, b }) => doc.setTextColor(r, g, b);
  const fill = ({ r, g, b }) => doc.setFillColor(r, g, b);
  const drawColor = ({ r, g, b }) => doc.setDrawColor(r, g, b);

  /* Text alignment helper for RTL */
  const tx = (text, x, yy, opts = {}) => {
    const { align = isAR ? "right" : "left", maxWidth } = opts;
    const xPos = align === "right" ? (PW - MR) : x;
    if (maxWidth) {
      doc.text(text, xPos, yy, { align, maxWidth });
    } else {
      doc.text(text, xPos, yy, { align });
    }
  };

  /* ── HEADER BAND ──────────────────────────────────── */
  fill(BRAND); doc.rect(0, 0, PW, 18, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(255, 255, 255);
  // Brand name always LTR
  doc.text("NexusFlow", ML, 12);
  // Date right-aligned
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  const dateStr = new Date().toLocaleDateString(isAR ? "en-GB" : "en-US", { year:"numeric", month:"long", day:"numeric" });
  doc.text(dateStr, PW - MR, 12, { align: "right" });
  y = 30;

  /* ── REPORT TITLE ─────────────────────────────────── */
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  col({ r: 107, g: 114, b: 128 }); // gray-500
  const reportLabel = isAR ? "تقرير قرار الحملة" : "Campaign Decision Report";
  tx(reportLabel, ML, y);
  y += 7;

  /* Campaign name */
  doc.setFontSize(18);
  col({ r: 255, g: 255, b: 255 });
  // For Arabic use a fallback: jsPDF core doesn't render Arabic glyphs natively.
  // We render the Latin transliteration / keep English campaign name as-is,
  // and use a note for pure Arabic strings.
  const campName = campaign?.name || (isAR ? "Campaign" : "Campaign");
  tx(campName, ML, y);
  y += 6;

  if (campaign?.channel) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    col({ r: 156, g: 163, b: 175 }); // gray-400
    const channelLabel = isAR ? `القناة: ${channelDisplay(campaign.channel, "ar")}` : `Channel: ${channelDisplay(campaign.channel, "en")}`;
    tx(channelLabel, ML, y);
    y += 10;
  } else { y += 4; }

  /* ── DIVIDER ──────────────────────────────────────── */
  drawColor({ r: 75, g: 85, b: 99 });
  doc.setLineWidth(0.3);
  doc.line(ML, y, PW - MR, y);
  y += 10;

  /* ── VERDICT BADGE ────────────────────────────────── */
  const vc   = VERDICT_COLORS[decision.decision] || VERDICT_COLORS.CONTINUE;
  const vLabel = isAR
    ? { CONTINUE: "استمرار", PAUSE: "إيقاف مؤقت", STOP: "إيقاف" }[decision.decision] || decision.decision
    : decision.decision;

  // Badge background
  doc.setFillColor(vc.r, vc.g, vc.b, 0.15);
  doc.roundedRect(ML, y, CW, 22, 3, 3, "F");
  drawColor(vc); doc.setLineWidth(0.5);
  doc.roundedRect(ML, y, CW, 22, 3, 3, "S");

  // Verdict text
  doc.setFont("helvetica", "black");
  doc.setFontSize(20);
  doc.setTextColor(vc.r, vc.g, vc.b);
  doc.text(vLabel, PW / 2, y + 14, { align: "center" });

  if (decision.status) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    col({ r: 156, g: 163, b: 175 });
    doc.text(decision.status, PW / 2, y + 20, { align: "center" });
  }
  y += 30;

  /* ── PATH BADGE (G1) ──────────────────────────────── */
  if (decision.pathBadge) {
    const badgeLabel = isAR ? (decision.pathBadge.labelAr || decision.pathBadge.label) : decision.pathBadge.label;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    col(BRAND);
    const prefix = isAR ? "المسار: " : "Basis: ";
    tx(`${prefix}${badgeLabel}`, ML, y);
    y += 8;
  }

  /* ── INPUTS SUMMARY (G3) ──────────────────────────── */
  if (Array.isArray(decision.inputsSummary) && decision.inputsSummary.length > 0) {
    const parts = decision.inputsSummary.map(it => {
      const lbl = isAR ? it.labelAr : it.label;
      const unit = it.unit === "SAR" ? (isAR ? "ر.س" : "SAR") : (it.unit || "");
      return `${lbl}: ${it.value}${unit ? " " + unit : ""}`;
    });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    col({ r: 156, g: 163, b: 175 });
    const summaryLine = parts.join("   |   ");
    const lines = doc.splitTextToSize(summaryLine, CW);
    lines.forEach(line => {
      tx(line, ML, y);
      y += 5;
    });
    y += 5;
  }

  /* ── KPI TABLE (G2) — prefer kpisDisplay, else legacy filtered ── */
  const KPI_META_SKIP = new Set([
    'spend','revenue','leads','conversions','targetCPL','targetCPA',
    'grossProfit','returnsLoss','profitWarning','profitWarningMsg',
    'revenueEst','revenueActual','ltv','ltvToCac','roi',
    'cpm','conversionRate','avgTicket','fees','shipping','profitMargin','returnRate',
  ]);
  const kpiEntries = Array.isArray(decision.kpisDisplay) && decision.kpisDisplay.length > 0
    ? decision.kpisDisplay.map(k => [k.key, k.value, (isAR ? k.labelAr : k.label)])
    : (decision.kpis
        ? Object.entries(decision.kpis)
            .filter(([k, v]) => v !== null && v !== undefined && !KPI_META_SKIP.has(k))
            .map(([k, v]) => [k, v, getKPILabel(k, lang)])
        : []);
  if (kpiEntries.length > 0) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    col({ r: 255, g: 255, b: 255 });
    const kpiTitle = isAR ? "مؤشرات الأداء الرئيسية" : "Key Performance Indicators";
    tx(kpiTitle, ML, y);
    y += 7;

    /* Draw table */
    const cols      = Math.min(kpiEntries.length, 4);
    const cellW     = CW / cols;
    const cellH     = 18;

    kpiEntries.slice(0, 8).forEach(([ key, val, label ], i) => {
      const col_i = i % cols;
      const row_i = Math.floor(i / cols);
      const cx    = ML + col_i * cellW;
      const cy    = y + row_i * (cellH + 3);

      // Cell background
      doc.setFillColor(31, 41, 55); // gray-800
      doc.roundedRect(cx, cy, cellW - 2, cellH, 2, 2, "F");

      // Label
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      col({ r: 156, g: 163, b: 175 });
      if (isAR) {
        doc.text(label, cx + cellW - 3, cy + 6, { align: "right" });
      } else {
        doc.text(label, cx + 4, cy + 6);
      }

      // Value
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      const isNeg = typeof val === "number" && val < 0;
      const valColor = isNeg ? { r:239,g:68,b:68 } : { r:255,g:255,b:255 };
      col(valColor);
      const fv = fmtKPI(key, val, lang);
      if (isAR) {
        doc.text(fv, cx + cellW - 3, cy + 14, { align: "right" });
      } else {
        doc.text(fv, cx + 4, cy + 14);
      }
    });

    const rows = Math.ceil(Math.min(kpiEntries.length, 8) / cols);
    y += rows * (cellH + 3) + 8;
  }

  /* Check page break */
  const checkY = (needed = 30) => {
    if (y + needed > PH - 20) { doc.addPage(); y = 20; }
  };

  /* ── ANALYSIS ─────────────────────────────────────── */
  if (decision.reason) {
    checkY(40);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    col({ r: 255, g: 255, b: 255 });
    const analysisTitle = isAR ? "التحليل" : "Analysis";
    tx(analysisTitle, ML, y);
    y += 6;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    col({ r: 209, g: 213, b: 219 }); // gray-300

    // jsPDF text wrapping — splitTextToSize handles Latin text well
    const lines = doc.splitTextToSize(decision.reason, CW);
    if (isAR) {
      lines.forEach(line => {
        checkY(6);
        doc.text(line, PW - MR, y, { align: "right" });
        y += 5.5;
      });
    } else {
      lines.forEach(line => {
        checkY(6);
        doc.text(line, ML, y);
        y += 5.5;
      });
    }
    y += 8;
  }

  /* ── RECOMMENDED ACTIONS ──────────────────────────── */
  if (decision.nextActions?.length > 0) {
    checkY(30);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    col({ r: 255, g: 255, b: 255 });
    const actTitle = isAR ? "الإجراءات الموصى بها" : "Recommended Actions";
    tx(actTitle, ML, y);
    y += 7;

    decision.nextActions.forEach((action, i) => {
      checkY(12);
      // Number circle
      doc.setFillColor(59, 130, 246, 0.3);
      doc.circle(isAR ? PW - MR - 3 : ML + 3, y - 1, 3, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7);
      col({ r: 96, g: 165, b: 250 }); // blue-400
      doc.text(String(i + 1), isAR ? PW - MR - 3 : ML + 3, y + 1, { align: "center" });

      // Action text
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      col({ r: 209, g: 213, b: 219 });
      const textX   = isAR ? PW - MR - 9 : ML + 8;
      const textW   = CW - 9;
      const lines   = doc.splitTextToSize(action, textW);
      lines.forEach(line => {
        checkY(6);
        if (isAR) {
          doc.text(line, textX, y, { align: "right" });
        } else {
          doc.text(line, textX, y);
        }
        y += 5.5;
      });
      y += 2;
    });
    y += 4;
  }

  /* ── NEXT REVIEW ──────────────────────────────────── */
  if (decision.nextReviewDays) {
    checkY(14);
    doc.setFillColor(31, 41, 55);
    doc.roundedRect(ML, y, CW, 12, 2, 2, "F");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    col({ r: 156, g: 163, b: 175 });
    const reviewLabel = isAR
      ? `المراجعة التالية: ${decision.nextReviewDays} يوم`
      : `Next Review: ${decision.nextReviewDays} days`;
    tx(reviewLabel, ML + 4, y + 8);
    y += 18;
  }

  /* ── FOOTER ───────────────────────────────────────── */
  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    doc.setFillColor(17, 24, 39); // gray-900
    doc.rect(0, PH - 14, PW, 14, "F");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    col({ r: 107, g: 114, b: 128 });
    doc.text("NexusFlow — nexusflow.io", ML, PH - 5);
    doc.text(`${p} / ${totalPages}`, PW - MR, PH - 5, { align: "right" });
  }

  /* ── SAVE ─────────────────────────────────────────── */
  const safeName = (campaign?.name || "decision")
    .replace(/[^a-zA-Z0-9\u0600-\u06FF\s-]/g, "")
    .replace(/\s+/g, "-")
    .substring(0, 40);
  const dateTag  = new Date().toISOString().split("T")[0];
  doc.save(`nexusflow-${safeName}-${dateTag}.pdf`);
}

/* ── Hook ────────────────────────────────────────────────── */
export function usePDFExport() {
  const [generating, setGenerating] = useState(false);
  const [error,      setError]      = useState(null);

  const generatePDF = useCallback(async (decision, campaign = null, lang = "en") => {
    setGenerating(true);
    setError(null);
    try {
      // ── Server-side endpoint (the only correct path) ──
      // The Arabic PDF needs Chromium-rendered HTML to handle bidi
      // and OpenType shaping correctly. The client-side jsPDF
      // fallback below cannot do that, so we keep it strictly as a
      // last-resort *English-only* path.
      //
      // Accept either `id` (saved decision read from Firestore) or
      // `decisionId` (freshly-returned shape from evaluateCampaign)
      // so this works in both the result modal and the history list.
      const decId = decision?.id || decision?.decisionId;

      const { getAuth } = await import("firebase/auth");
      const auth        = getAuth();
      const user        = auth.currentUser;

      if (user && decId) {
        const token    = await user.getIdToken();
        const baseUrl  = "https://us-central1-nexusflow-dev.cloudfunctions.net";
        const response = await fetch(
          `${baseUrl}/getDecisionPDF?id=${decId}&lang=${lang}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.ok) {
          const contentType = response.headers.get('content-type') || '';
          const isHTML = contentType.includes('text/html');
          const blob   = await response.blob();
          const url    = URL.createObjectURL(blob);
          const a      = document.createElement("a");
          const safeName = (campaign?.name || "decision")
            .replace(/[^a-zA-Z0-9\u0600-\u06FF\s-]/g, "")
            .replace(/\s+/g, "-")
            .substring(0, 40);
          a.href     = url;
          a.download = isHTML
            ? `nexusflow-${safeName}-${new Date().toISOString().split("T")[0]}.html`
            : `nexusflow-${safeName}-${new Date().toISOString().split("T")[0]}.pdf`;
          a.click();
          URL.revokeObjectURL(url);
          return; // ✅ server response succeeded
        }

        // Server returned an error. Surface it to the user — for
        // Arabic we DO NOT fall back to the broken client builder.
        // Quota exceeded → 429, feature disabled → 403.
        let errMsg = "PDF generation failed";
        try {
          const body = await response.json();
          errMsg = body?.message || body?.error || errMsg;
        } catch { /* non-JSON error body, keep default */ }

        if (lang === "ar" || response.status === 403 || response.status === 429) {
          // Arabic must come from the server. 403/429 are policy
          // errors that the user needs to see — don't silently
          // re-render with jsPDF.
          throw new Error(errMsg);
        }
        // For English, fall through to the client builder below as a
        // best-effort backup (e.g. transient network issue).
      }

      // ── Client-side fallback (English only, last resort) ─
      if (lang === "ar") {
        // No id, or no auth — the modal could be shown before the
        // first save in some flows. We refuse rather than emit a
        // broken Arabic PDF.
        throw new Error("Could not generate PDF — please try again from the saved decision.");
      }
      await buildPDF(decision, campaign, lang);
    } catch (err) {
      setError(err.message || "PDF generation failed");
    } finally {
      setGenerating(false);
    }
  }, []);

  return { generatePDF, generating, error };
}